import { useEffect, useCallback } from "react";

import {
    APICallStatus,
    useCCPPriceFinderData
} from "@/contentful/product-catalog.tsx";

import {
    PricingCalculatorContextValue,
    usePricingCalculatorContext
} from "./context.tsx";
import { BillingCycle, CcpPricingData } from "./types.ts";

export function useUnitCount() {
    const { inputValues, changeInputValues } = usePricingCalculatorContext();
    const { unitCount } = inputValues;
    const changeUnitCount = (value: number) => {
        return changeInputValues({ unitCount: value });
    };
    return [unitCount, changeUnitCount] as const;
}

export function useBillingCycle(): [
    BillingCycle,
    (value: BillingCycle) => void
] {
    const { inputValues, changeInputValues } = usePricingCalculatorContext();
    const { billingCycle } = inputValues;
    const changeBillingCycle = (value: BillingCycle) => {
        return changeInputValues({ billingCycle: value });
    };
    return [billingCycle, changeBillingCycle];
}

export function useCalculationResult() {
    return usePricingCalculatorContext().calculationResult;
}

export const useSelector = <T = unknown>(
    selector: (context: PricingCalculatorContextValue) => T
): T => {
    const context = usePricingCalculatorContext();
    return selector(context);
};

export interface UseCcpPricingDataProps {
    offeringSkus: string[];
    ssrData?: CcpPricingData;
}
export interface UseCcpPricingDataResult {
    data: CcpPricingData;
    status: UseCcpPricingDataStatus;
}
export type UseCcpPricingDataStatus = "pending" | "ready" | "error";
const PRODUCT_CATALOG_DELIVERY_KEY = process.env[
    "PRODUCT_CATALOG_DELIVERY_KEY"
] as string;
const PRODUCT_CATALOG_SPACE = process.env["PRODUCT_CATALOG_SPACE"] as string;
export const useCcpPricingData = (
    props: UseCcpPricingDataProps
): UseCcpPricingDataResult => {
    const { offeringSkus, ssrData } = props;

    const {
        fetchData,
        status: fetchStatus,
        prices: clientSideData
    } = useCCPPriceFinderData(
        offeringSkus,
        PRODUCT_CATALOG_DELIVERY_KEY,
        PRODUCT_CATALOG_SPACE
    );

    const execFetch = useCallback(async () => {
        await fetchData();
    }, []);

    useEffect(() => {
        if (!ssrData) {
            void execFetch();
        }
    }, []);

    const status = getCcpPricingDataStatus({
        hasSsrData: !!ssrData,
        fetchStatus
    });

    return {
        data: (ssrData as CcpPricingData) ?? clientSideData,
        status
    };
};

export interface GetCcpPricingDataStatusArgs {
    fetchStatus: APICallStatus;
    hasSsrData?: boolean;
}
function getCcpPricingDataStatus(
    args: GetCcpPricingDataStatusArgs
): UseCcpPricingDataStatus {
    const { fetchStatus, hasSsrData } = args;
    if (hasSsrData || fetchStatus === "complete") {
        return "ready";
    }
    if (fetchStatus === "idle" || fetchStatus === "fetching") {
        return "pending";
    }
    return "error";
}
